import { Link } from 'react-router-dom'
import { ArrowRight, TrendingUp, Users, Award, CheckCircle, Globe, Smartphone, Zap, Search, Users2, TrendingUp as TrendingUpIcon, Calendar, Calculator, DollarSign, Share2, BarChart3, BookOpen, Target, FileText, MessageCircle, Settings, ExternalLink } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect, useMemo, useRef } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import { Helmet } from 'react-helmet-async'
import translations from '../locales/translations'
import CalComModal from '../components/CalComModal'
import { useMarqueeScroll } from '../hooks/useMarqueeScroll'
import { getImageDimensions } from '../utils/imageDimensions'

const Home = () => {
  const { t, language } = useLanguage()
  const homeContent = useMemo(() => (translations as any)[language].home, [language])
  const [isCalComModalOpen, setIsCalComModalOpen] = useState(false)
  const testimonialsMarqueeRef = useMarqueeScroll()
  const testimonialsAutoScrollRef = useRef<{
    animationId: number | null
    lastTimestamp: number | null
    resumeTimeout: number | null
    isPaused: boolean
  }>({
    animationId: null,
    lastTimestamp: null,
    resumeTimeout: null,
    isPaused: false
  })
  
  const heroMessages = homeContent.hero.messages as Array<{ title: string; description: string }>

  const [currentMessageIndex, setCurrentMessageIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % heroMessages.length)
    }, 5000) // 5 saniye

    return () => clearInterval(interval)
  }, [heroMessages.length])

  useEffect(() => {
    const container = testimonialsMarqueeRef.current
    if (!container) return

    const mediaQuery = window.matchMedia('(max-width: 768px)')
    if (!mediaQuery.matches) {
      return
    }

    const state = testimonialsAutoScrollRef.current
    state.isPaused = false
    state.lastTimestamp = null

    const SPEED_PX_PER_SEC = 30

    const step = (timestamp: number) => {
      if (!container) return

      if (state.lastTimestamp === null) {
        state.lastTimestamp = timestamp
      }

      const delta = timestamp - (state.lastTimestamp ?? timestamp)
      state.lastTimestamp = timestamp

      if (!state.isPaused) {
        container.scrollLeft += (delta / 1000) * SPEED_PX_PER_SEC

        const maxScroll = container.scrollWidth - container.clientWidth
        if (maxScroll > 0 && container.scrollLeft >= maxScroll - 1) {
          container.scrollLeft = 0
        }
      }

      state.animationId = requestAnimationFrame(step)
    }

    const pause = () => {
      state.isPaused = true
      if (state.resumeTimeout) {
        window.clearTimeout(state.resumeTimeout)
        state.resumeTimeout = null
      }
    }

    const scheduleResume = () => {
      if (state.resumeTimeout) {
        window.clearTimeout(state.resumeTimeout)
      }
      state.resumeTimeout = window.setTimeout(() => {
        state.isPaused = false
      }, 2000)
    }

    const handleTouchStart = () => {
      pause()
    }

    const handleTouchMove = () => {
      pause()
    }

    const handleTouchEnd = () => {
      scheduleResume()
    }

    container.addEventListener('touchstart', handleTouchStart, { passive: true })
    container.addEventListener('touchmove', handleTouchMove, { passive: true })
    container.addEventListener('touchend', handleTouchEnd, { passive: true })

    state.animationId = requestAnimationFrame(step)

    return () => {
      if (state.animationId) {
        cancelAnimationFrame(state.animationId)
        state.animationId = null
      }
      if (state.resumeTimeout) {
        window.clearTimeout(state.resumeTimeout)
        state.resumeTimeout = null
      }
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
    }
  }, [language, testimonialsMarqueeRef])
  const services = useMemo(() => {
    const servicesData = homeContent.services
    return [
    {
      icon: TrendingUp,
        title: servicesData.googleAds.title,
        description: servicesData.googleAds.description,
        link: '/services/google-ads',
        features: servicesData.googleAds.features || [],
        details: servicesData.googleAds.details,
        color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Users,
        title: servicesData.metaAds.title,
        description: servicesData.metaAds.description,
        link: '/services/meta-ads',
        features: servicesData.metaAds.features || [],
        details: servicesData.metaAds.details,
        color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Award,
        title: servicesData.socialMedia.title,
        description: servicesData.socialMedia.description,
        link: '/services/social-media',
        features: servicesData.socialMedia.features || [],
        details: servicesData.socialMedia.details,
        color: 'from-pink-500 to-pink-600'
    },
    {
      icon: Globe,
        title: servicesData.webDesign.title,
        description: servicesData.webDesign.description,
        link: '/services/web-design',
        features: servicesData.webDesign.features || [],
        details: servicesData.webDesign.details,
        color: 'from-green-500 to-green-600'
      },
      {
        icon: Search,
        title: servicesData.seoGeo.title,
        description: servicesData.seoGeo.description,
        link: '/services/seo-geo',
        features: servicesData.seoGeo.features || [],
        details: servicesData.seoGeo.details,
        color: 'from-teal-500 to-teal-600'
      },
      {
        icon: Smartphone,
        title: servicesData.appDevelopment.title,
        description: servicesData.appDevelopment.description,
        link: '/services/app-development',
        features: servicesData.appDevelopment.features || [],
        details: servicesData.appDevelopment.details,
        color: 'from-orange-500 to-orange-600'
      }
    ]
  }, [homeContent])

  const servicesSection = homeContent.servicesSection || {}
  const marketingToolsContent = homeContent.marketingTools || {}

  const marketingCategoryIcons: Record<string, any> = useMemo(
    () => ({
      'roas-roi': TrendingUp,
      influencer: Users,
      budget: DollarSign,
      social: Share2,
      ecommerce: BarChart3,
      content: BookOpen
    }),
    []
  )

  const marketingCategories = useMemo(() => {
    const categories = (marketingToolsContent.categories as Array<any>) || []
    return categories.map((category) => ({
      ...category,
      tools: category.tools || [],
      icon: marketingCategoryIcons[category.id] || Calculator
    }))
  }, [marketingToolsContent, marketingCategoryIcons])



  return (
    <>
      <Helmet>
        <title>{language === 'tr' ? 'Thinklyze - Dijital Performans Pazarlama Ajansı | SEO, SEM, Social Media' : language === 'en' ? 'Thinklyze - Digital Performance Marketing Agency | SEO, SEM, Social Media' : 'Thinklyze - Агентство цифрового маркетинга | SEO, SEM, Social Media'}</title>
        <meta name="description" content={language === 'tr' ? 'Dijital pazarlama, performans reklamcılığı ve veri analitiği ile markanızı dijitalde güçlendirin. Google Ads, Meta Ads, SEO, Web Tasarım hizmetleri. İstanbul, Türkiye.' : language === 'en' ? 'Empower your brand digitally with digital marketing, performance advertising, and data analytics. Google Ads, Meta Ads, SEO, Web Design services. Istanbul, Turkey.' : 'Усиливайте свой бренд в цифровом пространстве с помощью цифрового маркетинга, performance рекламы и аналитики данных. Google Ads, Meta Ads, SEO, веб-дизайн. Стамбул, Турция.'} />
        <meta name="keywords" content={language === 'tr' ? 'dijital pazarlama, SEO, SEM, Google Ads, Meta Ads, Facebook Ads, Instagram Ads, social media marketing, performans pazarlama, reklam yönetimi, web tasarım, e-ticaret, pazarlama ajansı, İstanbul, Türkiye' : 'digital marketing, SEO, SEM, Google Ads, Meta Ads, Facebook Ads, Instagram Ads, social media marketing, performance marketing, ad management, web design, e-commerce, marketing agency, Istanbul, Turkey'} />
        <meta property="og:title" content={language === 'tr' ? 'Thinklyze - Dijital Performans Pazarlama Ajansı' : 'Thinklyze - Digital Performance Marketing Agency'} />
        <meta property="og:description" content={language === 'tr' ? 'Dijital pazarlama, performans reklamcılığı ve veri analitiği ile markanızı dijitalde güçlendirin.' : 'Empower your brand digitally with digital marketing, performance advertising, and data analytics.'} />
        <meta property="og:image" content="https://thinklyze.com/images/logo_siyah.webp" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thinklyze.com/" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={language === 'tr' ? 'Thinklyze - Dijital Performans Pazarlama Ajansı' : 'Thinklyze - Digital Performance Marketing Agency'} />
        <meta name="twitter:description" content={language === 'tr' ? 'Dijital pazarlama, performans reklamcılığı ve veri analitiği ile markanızı dijitalde güçlendirin.' : 'Empower your brand digitally with digital marketing, performance advertising, and data analytics.'} />
        <meta name="geo.region" content="TR-34" />
        <meta name="geo.placename" content="İstanbul" />
        <meta name="geo.position" content="41.0082;28.9784" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Thinklyze",
            "description": language === 'tr' ? "Dijital performans pazarlama ajansı. SEO, SEM, Social Media ve dijital pazarlama hizmetleri." : "Digital performance marketing agency. SEO, SEM, Social Media and digital marketing services.",
            "url": "https://thinklyze.com",
            "logo": "https://thinklyze.com/images/logo_siyah.webp",
            "image": "https://thinklyze.com/images/logo_siyah.webp",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Zeytinlik Mah. Fişekhane Cad. Carousel No: 5 İç Kapı No: 17",
              "addressLocality": "Bakırköy",
              "addressRegion": "İstanbul",
              "postalCode": "34140",
              "addressCountry": "TR"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 41.0082,
              "longitude": 28.9784
            },
            "telephone": "+90-212-123-45-67",
            "email": "info@thinklyze.com",
            "priceRange": "$$",
            "serviceArea": {
              "@type": "Country",
              "name": "Turkey"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": language === 'tr' ? "Dijital Pazarlama Hizmetleri" : "Digital Marketing Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": language === 'tr' ? "Google Ads Yönetimi" : "Google Ads Management",
                    "description": language === 'tr' ? "Google Ads kampanya yönetimi ve optimizasyonu" : "Google Ads campaign management and optimization"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": language === 'tr' ? "Meta Ads Yönetimi" : "Meta Ads Management",
                    "description": language === 'tr' ? "Facebook ve Instagram reklam yönetimi" : "Facebook and Instagram ad management"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": language === 'tr' ? "SEO Optimizasyonu" : "SEO Optimization",
                    "description": language === 'tr' ? "Arama motoru optimizasyonu hizmetleri" : "Search engine optimization services"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": language === 'tr' ? "Web Tasarım" : "Web Design",
                    "description": language === 'tr' ? "Modern ve responsive web tasarım hizmetleri" : "Modern and responsive web design services"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>
    <div className="pt-16">
      {/* Hero Section */}
      <section className="bg-white pt-20 pb-12">
        <div className="max-w-5xl mx-auto px-6">
            <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            className="text-center"
            >
            <div className="inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium mb-4 font-jakarta relative min-h-[30px] min-w-[200px]">
              <AnimatePresence mode="wait">
                <motion.span
                  key={currentMessageIndex}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="inline-block"
                >
                  {services[currentMessageIndex % services.length]?.title || t('home.hero.newRelease')}
                </motion.span>
              </AnimatePresence>
              </div>
              
            <div className="relative min-h-[100px] md:min-h-[120px] mb-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentMessageIndex}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 flex flex-col items-center justify-center"
                >
                  <h1 className="text-[25px] md:text-[40px] font-bold text-gray-900 leading-tight max-w-4xl mx-auto font-jakarta text-center">
                    {heroMessages[currentMessageIndex].title}
              </h1>
                </motion.div>
              </AnimatePresence>
            </div>
            
            <div className="relative min-h-[70px] md:min-h-[85px] mb-10 sm:mb-12">
              <AnimatePresence mode="wait">
                <motion.p
                  key={currentMessageIndex}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto font-jakarta text-center absolute inset-0 flex items-center justify-center"
                >
                  {heroMessages[currentMessageIndex].description}
                </motion.p>
              </AnimatePresence>
            </div>
            
            <div className="flex flex-col gap-2 sm:gap-3 justify-center items-center mb-16 sm:mb-10 px-4">
                <div className="flex flex-row gap-2 sm:gap-3 justify-center items-center w-full">
                <Link 
                  to="/contact" 
                    className="bg-white text-gray-900 border border-gray-300 px-4 sm:px-5 py-2.5 rounded-lg text-sm font-normal hover:bg-gray-50 transition-colors duration-200 inline-flex items-center justify-center gap-2 font-jakarta flex-1 sm:flex-initial min-w-[140px] sm:min-w-0 whitespace-nowrap"
                >
                    <span className="whitespace-nowrap">{t('home.hero.contactButton')}</span>
                </Link>
                <Link 
                  to="/services" 
                    className="bg-white text-gray-900 border border-gray-300 px-4 sm:px-5 py-2.5 rounded-lg text-sm font-normal hover:bg-gray-50 transition-colors duration-200 inline-flex items-center justify-center gap-2 font-jakarta flex-1 sm:flex-initial min-w-[140px] sm:min-w-0 whitespace-nowrap"
                >
                    <span className="whitespace-nowrap">{t('home.hero.exploreServices')}</span>
                    <span className="text-base flex-shrink-0">▷</span>
                </Link>
              </div>
                      <button
                  onClick={() => setIsCalComModalOpen(true)}
                  className="bg-black text-white px-4 sm:px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors duration-200 inline-flex items-center justify-center gap-2 font-jakarta w-full sm:w-auto min-w-[140px] sm:min-w-0"
                >
                  <Calendar className="w-4 h-4 flex-shrink-0" />
                  <span>{t('servicePages.common.scheduleMeeting')}</span>
                      </button>
                </div>

            {/* Google Reviews Widget */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-6"
            >
              <a
                href="https://www.google.com/search?q=Thinklyze&hl=tr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 sm:gap-3 bg-white border border-gray-200 rounded-lg px-3 sm:px-5 py-2 sm:py-3 hover:border-gray-300 hover:shadow-md transition-all duration-300 group"
              >
                <div className="flex items-center gap-0.5 sm:gap-1">
                  {[...Array(5)].map((_, index) => (
                    <svg
                      key={index}
                      className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400 fill-current"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                    ))}
                    </div>
                <div className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap">
                  <span className="text-xs sm:text-sm font-semibold text-gray-900">5.0</span>
                  <span className="text-xs sm:text-sm text-gray-600">·</span>
                  <span className="text-xs sm:text-sm text-gray-600">
                    {language === 'tr' ? '150 değerlendirmeden' : language === 'en' ? '150 reviews' : '150 отзывов'}
                  </span>
                    </div>
                <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
              </a>
            </motion.div>

            {/* References Section */}
            <div className="flex flex-col items-center gap-5 mt-12">
              {/* Client Logos Grid - Two Rows */}
              <div className="w-full overflow-hidden py-4 relative">
                {/* Top Row - Slides Right */}
                <div className="overflow-hidden mb-4 relative">
                  {/* Fade gradients */}
                  <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
                  <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
                  <div className="flex animate-marquee-right gap-4">
                    {/* First set */}
                    {[
                      '/images/brands/ref1.webp',
                      '/images/brands/ref2.webp',
                      '/images/brands/ref3.webp',
                      '/images/brands/ref4.webp',
                      '/images/brands/ref8.webp',
                      '/images/brands/ref6.webp',
                      '/images/brands/ref7.webp',
                    ].map((logo, index) => {
                      const dimensions = getImageDimensions(logo)
                      return (
                        <div key={`top-${index}`} className="flex-shrink-0 bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                          <img 
                            src={logo} 
                            alt={`Referans ${index + 1}`} 
                            loading="lazy"
                            decoding="async"
                            width={dimensions?.width}
                            height={dimensions?.height}
                            className="h-8 sm:h-12 max-w-[100px] sm:max-w-[140px] object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                          />
                        </div>
                      )
                    })}
                    {/* Duplicate set for seamless loop */}
                    {[
                      '/images/brands/ref1.webp',
                      '/images/brands/ref2.webp',
                      '/images/brands/ref3.webp',
                      '/images/brands/ref4.webp',
                      '/images/brands/ref8.webp',
                      '/images/brands/ref6.webp',
                      '/images/brands/ref7.webp',
                    ].map((logo, index) => {
                      const dimensions = getImageDimensions(logo)
                      return (
                        <div key={`top-dup-${index}`} className="flex-shrink-0 bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                          <img 
                            src={logo} 
                            alt={`Referans ${index + 1}`} 
                            loading="lazy"
                            decoding="async"
                            width={dimensions?.width}
                            height={dimensions?.height}
                            className="h-8 sm:h-12 max-w-[100px] sm:max-w-[140px] object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>

                {/* Bottom Row - Slides Left */}
                <div className="overflow-hidden relative">
                  {/* Fade gradients */}
                  <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
                  <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>
                  <div className="flex animate-marquee-left gap-4">
                    {/* First set */}
                    {[
                      '/images/brands/veela1.webp',
                      '/images/brands/daphnesandals.webp',
                      '/images/brands/ref1.webp',
                      '/images/brands/ref2.webp',
                      '/images/brands/ref3.webp',
                      '/images/brands/ref4.webp',
                    ].map((logo, index) => {
                      const dimensions = getImageDimensions(logo)
                      return (
                        <div key={`bottom-${index}`} className="flex-shrink-0 bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                          <img 
                            src={logo} 
                            alt={`Referans ${index + 1}`} 
                            loading="lazy"
                            decoding="async"
                            width={dimensions?.width}
                            height={dimensions?.height}
                            className="h-8 sm:h-12 max-w-[100px] sm:max-w-[140px] object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                          />
                        </div>
                      )
                    })}
                {/* Duplicate set for seamless loop */}
                    {[
                      '/images/brands/veela1.webp',
                      '/images/brands/daphnesandals.webp',
                      '/images/brands/ref1.webp',
                      '/images/brands/ref2.webp',
                      '/images/brands/ref3.webp',
                      '/images/brands/ref4.webp',
                    ].map((logo, index) => {
                      const dimensions = getImageDimensions(logo)
                      return (
                        <div key={`bottom-dup-${index}`} className="flex-shrink-0 bg-white rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200">
                          <img 
                            src={logo} 
                            alt={`Referans ${index + 1}`} 
                            loading="lazy"
                            decoding="async"
                            width={dimensions?.width}
                            height={dimensions?.height}
                            className="h-8 sm:h-12 max-w-[100px] sm:max-w-[140px] object-contain grayscale opacity-70 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                />
              </div>
                      )
                    })}
            </div>
          </div>
          </div>

              {/* Title Below Logos */}
              <div className="text-center mt-4">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gray-50 rounded-full border border-gray-100">
                  <TrendingUpIcon className="w-4 h-4 text-gray-400" />
                  <p className="text-sm md:text-base text-gray-700 font-medium font-jakarta">
                    {t('home.references.title')}
                  </p>
              </div>
            </div>
          </div>
            </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight font-jakarta">
              {servicesSection.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed font-jakarta">
              {servicesSection.subtitle}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
            <motion.div
                key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Link
                  to={service.link}
                  className="group bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100 hover:border-transparent hover:shadow-2xl transition-all duration-300 h-full flex flex-col relative overflow-hidden block"
                >
                  {/* Gradient background on hover */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>
                  
                  <div className="relative z-10">
                    <div className="flex items-start mb-3 sm:mb-5">
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg group-hover:shadow-xl`}>
                        <service.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
              </div>
              
                    <h3 className="text-lg sm:text-xl font-bold text-[#111] mb-2 sm:mb-2.5 group-hover:text-gray-900 transition-colors">
                      {service.title}
              </h3>
              
                    <p className="text-[#555] text-xs sm:text-sm leading-relaxed mb-4 sm:mb-5 flex-grow">
                      {service.description}
                    </p>
                
                    {/* Features List */}
                    <div className="space-y-2 sm:space-y-2.5 mb-4 sm:mb-5 border-t border-gray-100 pt-3 sm:pt-5">
                      {(service.features as string[]).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 sm:gap-3">
                          <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-gradient-to-br ${service.color} opacity-10 group-hover:opacity-20 flex items-center justify-center flex-shrink-0 transition-opacity`}>
                            <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-700" />
                    </div>
                          <span className="text-xs sm:text-sm text-[#555]">{feature}</span>
                  </div>
              ))}
                </div>
                
                    <div className="flex items-center justify-end pt-2 sm:pt-3 border-t border-gray-100">
                      <div className="flex items-center text-gray-700 text-xs sm:text-sm font-semibold group-hover:gap-2 transition-all">
                        {service.details || t('common.learnMore')}
                        <ArrowRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 ml-1 group-hover:ml-0 group-hover:translate-x-1 transition-all" />
                </div>
                  </div>
                </div>
                </Link>
            </motion.div>
            ))}
          </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mt-10"
          >
            <Link to="/services" className="bg-black hover:bg-gray-800 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 inline-flex items-center gap-2 font-jakarta">
              {homeContent.viewAllServices || t('home.viewAllServices')}
              <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
      </section>

      {/* Marketing Tools Section */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
            >
            <div className="inline-flex items-center bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg text-xs font-medium mb-4 font-jakarta">
              <Calculator className="w-4 h-4 mr-2" />
              {marketingToolsContent.badge}
              </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight font-jakarta">
              {marketingToolsContent.title}
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed font-jakarta">
              {marketingToolsContent.description}
              </p>
            </motion.div>

          {/* Calculator Categories Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {marketingCategories.map((category, index) => {
              const IconComponent = category.icon
              return (
            <motion.div
                  key={category.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="flex flex-col bg-white border border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group h-full">
                    <div className="flex-1">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 group-hover:scale-110 transition-all duration-300">
                          <IconComponent className="w-6 h-6 text-blue-600 group-hover:text-blue-700 transition-colors" />
              </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1 font-jakarta group-hover:text-blue-600 transition-colors">
                            {category.name}
                          </h3>
                          <p className="text-sm text-gray-600 font-jakarta">
                            {category.description}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2 mb-4">
                        {(category.tools as string[]).slice(0, 3).map((tool, toolIndex) => (
                          <div key={toolIndex} className="flex items-center gap-2 text-sm text-gray-700 font-jakarta">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span>{tool}</span>
                          </div>
                        ))}
                        {category.tools.length > 3 && (
                          <div className="text-xs text-gray-500 font-jakarta mt-2">
                            {(marketingToolsContent.moreToolsLabel || '').replace('{count}', String(category.tools.length - 3))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <Link
                        to="/marketing-tools"
                        className="text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200 text-sm font-jakarta inline-flex items-center gap-2 group/cta"
                      >
                        <span>Ücretsiz Hesaplama Başla</span>
                        <ArrowRight className="w-4 h-4 group-hover/cta:translate-x-1 transition-transform duration-200" />
                      </Link>
                    </div>
                  </div>
            </motion.div>
              )
            })}
          </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
              className="text-center"
            >
            <Link 
              to="/marketing-tools" 
              className="bg-black hover:bg-gray-800 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 inline-flex items-center gap-2 font-jakarta"
            >
              {marketingToolsContent.cta}
              <ArrowRight className="w-5 h-5" />
            </Link>
            </motion.div>
        </div>
      </section>

      {/* Digital Excellence Section */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Section - Company Introduction */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              {/* Team Indicator */}
              <div className="inline-flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
                <div className="flex items-center -space-x-2">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white">
                    <Users className="w-4 h-4 text-white" />
                </div>
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
                    <Users className="w-4 h-4 text-white" />
                </div>
                  <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center border-2 border-white">
                    <Users className="w-4 h-4 text-white" />
                </div>
                </div>
                <span className="text-sm text-gray-700 font-medium font-jakarta">{homeContent.digitalExcellence.teamBadge}</span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight font-jakarta">
                {homeContent.digitalExcellence.title}
              </h2>
              
              <p className="text-lg text-gray-600 leading-relaxed font-jakarta">
                {homeContent.digitalExcellence.description}
              </p>
              
              <Link 
                to="/services" 
                className="inline-flex items-center bg-black text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors duration-200 font-jakarta"
              >
                {homeContent.digitalExcellence.cta}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>

              {/* Statistics */}
              <div className="grid grid-cols-2 gap-6 pt-6 border-t border-gray-200">
                <div>
                  <div className="text-4xl font-bold text-gray-900 mb-2 font-jakarta">75%</div>
                  <div className="text-sm text-gray-600 font-jakarta">{homeContent.digitalExcellence.stats.conversion}</div>
                </div>
                <div>
                  <div className="text-4xl font-bold text-gray-900 mb-2 font-jakarta">30K+</div>
                  <div className="text-sm text-gray-600 font-jakarta">{homeContent.digitalExcellence.stats.customers}</div>
                </div>
              </div>
            </motion.div>

            {/* Right Section - Features Grid */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              {/* Feature Cards */}
              {[
                {
                  icon: Target,
                  iconColor: "text-blue-600",
                  iconBg: "bg-blue-50",
                  title: homeContent.digitalExcellence.features[0].title,
                  description: homeContent.digitalExcellence.features[0].description
                },
                {
                  icon: Zap,
                  iconColor: "text-yellow-600",
                  iconBg: "bg-yellow-50",
                  title: homeContent.digitalExcellence.features[1].title,
                  description: homeContent.digitalExcellence.features[1].description
                },
                {
                  icon: Globe,
                  iconColor: "text-green-600",
                  iconBg: "bg-green-50",
                  title: homeContent.digitalExcellence.features[2].title,
                  description: homeContent.digitalExcellence.features[2].description
                },
                {
                  icon: FileText,
                  iconColor: "text-purple-600",
                  iconBg: "bg-purple-50",
                  title: homeContent.digitalExcellence.features[3].title,
                  description: homeContent.digitalExcellence.features[3].description
                },
                {
                  icon: Settings,
                  iconColor: "text-gray-600",
                  iconBg: "bg-gray-50",
                  title: homeContent.digitalExcellence.features[4].title,
                  description: homeContent.digitalExcellence.features[4].description
                },
                {
                  icon: MessageCircle,
                  iconColor: "text-indigo-600",
                  iconBg: "bg-indigo-50",
                  title: homeContent.digitalExcellence.features[5].title,
                  description: homeContent.digitalExcellence.features[5].description
                }
              ].map((feature, index) => {
                const IconComponent = feature.icon
                return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white border border-gray-200 rounded-xl p-5 hover:border-gray-300 hover:shadow-md transition-all duration-300 group"
                  >
                    <div className={`w-10 h-10 ${feature.iconBg} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className={`w-5 h-5 ${feature.iconColor}`} />
                    </div>
                    <h3 className="text-base font-bold text-gray-900 mb-2 font-jakarta">
                    {feature.title}
                  </h3>
                    <p className="text-gray-600 text-xs leading-relaxed font-jakarta">
                    {feature.description}
                  </p>
                </motion.div>
                )
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advanced Features Section */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center bg-gray-100 text-gray-800 px-3 py-1.5 rounded-lg text-xs font-medium mb-4 font-jakarta">
              {homeContent.advancedFeatures.badge}
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight max-w-4xl mx-auto font-jakarta">
              {homeContent.advancedFeatures.title}
            </h2>
            
            <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto font-jakarta">
              {homeContent.advancedFeatures.subtitle}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Left Section - Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gray-100 text-gray-800 px-3 py-1 rounded-lg text-xs font-medium font-jakarta">
                {homeContent.advancedFeatures.sectionBadge}
              </div>
              
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight font-jakarta">
                {homeContent.advancedFeatures.sectionTitle}
              </h3>
              
              <p className="text-base text-gray-600 leading-relaxed font-jakarta">
                {homeContent.advancedFeatures.sectionDescription}
              </p>
              
              <Link 
                to="/services/web-design" 
                className="inline-flex items-center bg-black text-white px-6 py-3 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors duration-200 font-jakarta"
              >
                {homeContent.advancedFeatures.cta}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </motion.div>

            {/* Right Section - Portfolio Preview */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { id: 1, name: 'SmartIQ', preview: '/images/portfolio/smartiq.webp', url: 'http://smartiq.io/' },
                { id: 2, name: 'MCD Bag', preview: '/images/portfolio/mcdbag.webp', url: 'https://mcdbag.com/' },
                { id: 3, name: 'Veela', preview: '/images/portfolio/veela.webp', url: 'https://veela.com.tr/' },
                { id: 4, name: 'Daphne Sandals', preview: '/images/portfolio/daphnesandals.webp', url: 'https://daphnesandals.com/' }
              ].map((site, index) => (
          <motion.div
                  key={site.id}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group cursor-pointer"
                  onClick={() => window.open(site.url, '_blank')}
                >
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-lg transition-all duration-300">
                    <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
                      <img
                        src={site.preview}
                        alt={site.name}
                        loading="lazy"
                        decoding="async"
                        width={getImageDimensions(site.preview)?.width}
                        height={getImageDimensions(site.preview)?.height}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.src = `https://api.screenshotone.com/take?access_key=demo&url=${encodeURIComponent(site.url)}&viewport_width=1280&viewport_height=720&device_scale_factor=1&format=png&image_quality=80`
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                        <div className="p-3 w-full">
                          <div className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 text-xs font-medium text-gray-900 flex items-center justify-between">
                            <span>{site.name}</span>
                            <ExternalLink className="w-3 h-3" />
              </div>
            </div>
                  </div>
                </div>
              </div>
                </motion.div>
              ))}
          </motion.div>
        </div>

          {/* View All Portfolio Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-center"
          >
            <Link 
              to="/services/web-design" 
              className="inline-flex items-center text-gray-700 hover:text-gray-900 text-sm font-medium transition-colors duration-200 font-jakarta"
            >
              {homeContent.advancedFeatures.viewAllWebsites}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-[#f9f9f9] py-20 md:py-28 relative">
        {/* Subtle Grid Background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `
              linear-gradient(to right, #e5e7eb 1px, transparent 1px),
              linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)
            `,
            backgroundSize: '80px 80px',
            opacity: 0.3
          }}
        ></div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            {/* Tag */}
            <div className="inline-flex items-center gap-2 bg-gray-900 text-white rounded-full px-4 py-1.5 mb-6">
              <Users2 className="w-4 h-4" />
              <span className="text-sm font-medium">{t('testimonials.tag')}</span>
            </div>
            
            {/* Title */}
            <h2 className="text-[36px] font-bold text-gray-900 mb-5 leading-tight max-w-4xl mx-auto">
              {t('testimonials.title')}
            </h2>
            
            {/* Subtitle */}
            <p className="text-lg md:text-xl text-gray-600 mb-0 leading-relaxed max-w-3xl mx-auto">
              {t('testimonials.subtitle')}
            </p>
          </motion.div>

          {/* Testimonials Marquee */}
          <div
            ref={testimonialsMarqueeRef}
            className="w-full overflow-x-auto md:overflow-hidden py-4 relative marquee-container-mobile"
          >
            {/* Fade gradients - hidden on mobile */}
            <div className="hidden md:block absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-[#f9f9f9] to-transparent z-10 pointer-events-none"></div>
            <div className="hidden md:block absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-[#f9f9f9] to-transparent z-10 pointer-events-none"></div>
            <div className="flex gap-4 md:gap-6 animate-marquee-right px-6 md:px-0">
              {/* First set */}
              {((translations as any)[language].testimonials.items as Array<{ quote: string; name: string; title: string; avatar: string }>).map((testimonial, index) => (
                <div
                  key={`testimonial-${index}`}
                  className="marquee-item-mobile flex-shrink-0 w-[280px] md:w-[380px] bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col"
                >
                  <p className="text-base font-medium text-gray-900 mb-5 leading-relaxed flex-grow">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                    <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium text-sm">{testimonial.avatar}</span>
              </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 mb-0.5">
                        {testimonial.name}
                </div>
                      <div className="text-xs text-gray-600">
                        {testimonial.title}
              </div>
              </div>
          </div>
        </div>
              ))}
              {/* Duplicate set for seamless loop */}
              {((translations as any)[language].testimonials.items as Array<{ quote: string; name: string; title: string; avatar: string }>).map((testimonial, index) => (
                <div
                  key={`testimonial-dup-${index}`}
                  className="flex-shrink-0 w-[280px] md:w-[380px] bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col"
                >
                  <p className="text-base font-medium text-gray-900 mb-5 leading-relaxed flex-grow">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                    <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-medium text-sm">{testimonial.avatar}</span>
                  </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900 mb-0.5">
                        {testimonial.name}
                </div>
                      <div className="text-xs text-gray-600">
                        {testimonial.title}
                      </div>
                    </div>
                </div>
                </div>
            ))}
          </div>
          </div>
        </div>
      </section>

      {/* Cal.com Modal */}
      <CalComModal 
        isOpen={isCalComModalOpen} 
        onClose={() => setIsCalComModalOpen(false)} 
      />
    </div>
    </>
  )
}

export default Home
